import { useEffect, useState } from 'react';
import { api } from '../lib/api.js';
import { useBranding } from '../lib/branding.jsx';

/**
 * Branding is stored as loose key/value settings and applied at runtime, so
 * these values can be swapped later without touching any component. The logo
 * goes to R2 through the same presigned-PUT path as everything else.
 */
export default function BrandingPanel({ onClose }) {
  const { branding, reload } = useBranding();
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');

  useEffect(() => {
    if (branding && !form) {
      setForm({
        studio_name: branding.studio_name || '',
        accent_color: branding.accent_color || '#8a7358',
        footer_text: branding.footer_text || '',
        contact_email: branding.contact_email || '',
      });
    }
  }, [branding, form]);

  if (!form) return null;
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  async function save(extra = {}) {
    setBusy(true);
    setMsg('');
    try {
      await api.put('/api/admin/branding', { ...form, ...extra });
      await reload();
      setMsg('Saved');
    } catch (err) {
      setMsg(err.message);
    } finally {
      setBusy(false);
    }
  }

  async function uploadLogo(file) {
    if (!file) return;
    setBusy(true);
    try {
      const { key, url } = await api.post('/api/admin/branding/logo-url', {
        filename: file.name,
        contentType: file.type,
      });
      const res = await fetch(url, { method: 'PUT', body: file, headers: { 'Content-Type': file.type } });
      if (!res.ok) throw new Error('Logo upload failed');
      await save({ logo_key: key });
    } catch (err) {
      setMsg(err.message);
      setBusy(false);
    }
  }

  return (
    <div className="card stack">
      <div className="spread">
        <h2>Studio branding</h2>
        <button className="ghost sm" onClick={onClose}>
          Close
        </button>
      </div>
      <div className="row" style={{ alignItems: 'flex-start' }}>
        <div className="field grow">
          <label htmlFor="sn">Studio name</label>
          <input id="sn" value={form.studio_name} onChange={set('studio_name')} />
        </div>
        <div className="field" style={{ width: 130 }}>
          <label htmlFor="ac">Accent colour</label>
          <input id="ac" type="color" value={form.accent_color} onChange={set('accent_color')} style={{ padding: '0.2rem', height: 42 }} />
        </div>
      </div>
      <div className="field">
        <label htmlFor="ce">Contact email shown to clients</label>
        <input id="ce" type="email" value={form.contact_email} onChange={set('contact_email')} />
      </div>
      <div className="field">
        <label htmlFor="ft">Gallery footer text</label>
        <input id="ft" value={form.footer_text} onChange={set('footer_text')} />
      </div>
      <div className="field">
        <label>Logo</label>
        <div className="row">
          {branding?.logo_url && <img src={branding.logo_url} alt="" style={{ height: 34 }} />}
          <input type="file" accept="image/*" onChange={(e) => uploadLogo(e.target.files?.[0])} />
        </div>
      </div>
      <div className="row">
        <button className="primary" disabled={busy} onClick={() => save()}>
          {busy ? 'Saving…' : 'Save branding'}
        </button>
        {msg && <span className="muted">{msg}</span>}
      </div>
    </div>
  );
}
